import { h, Component } from 'preact'
import Phrase from '../../libs/translationsUtils/phrase'
import InputGroup from '../../control-components/ui-components/InputGroup'
import Icon from '../assets'
import axios from 'axios'
import { TimeRanges, allTimeRanges } from '../widget-points-leaderboard/indexWidget.sync'
import { isInputEl } from '../../libs/typeGuards'
import { ConfigurationPanel } from './ConfigurationPanel'
import Tip from '../../control-components/ui-components/Tip'
import DynamicImport from '../utils/DynamicImport'
const CodeMirror = DynamicImport(async () => await import('../../control-components/codemirror/index'))

export interface UserRole {
  isMain: boolean
  label: string
  name: string
}

interface Content {
  maxResults: number
  excludeUserRole: string[]
  timeRange: TimeRanges[]
  emptyMessage: string
  isPreviewEmptyMessage: boolean
  showLink: boolean
  linkText: string
  showLoggedInUserSection: boolean
}

interface NewData extends Content {
  title: string
}

interface State extends NewData {
  searchText: string
  inputInFocus: boolean
  allUserRoles: UserRole[]
  isContentPanelActive: boolean
  isUsersPanelActive: boolean
  isEmptyStatusPanelActive: boolean
}

interface Props {
  title: string
  controlUrl: string
  onChange: (state: NewData) => void
  content: Content
  allUserRoles?: UserRole[]
}

export default class LeaderboardPointsConfig extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    this.state = {
      title: props.title,
      maxResults: props.content.maxResults,
      excludeUserRole: props.content.excludeUserRole,
      timeRange: props.content.timeRange,
      inputInFocus: false,
      searchText: '',
      allUserRoles: props.allUserRoles ?? [],
      emptyMessage: props.content.emptyMessage,
      isPreviewEmptyMessage: props.content.isPreviewEmptyMessage,
      showLink: props.content.showLink,
      linkText: props.content.linkText || 'Show full leaderboard',
      showLoggedInUserSection: props.content.showLoggedInUserSection,
      isContentPanelActive: true,
      isUsersPanelActive: false,
      isEmptyStatusPanelActive: false
    }

    this.onChangeIsPreviewEmptyMessage = this.onChangeIsPreviewEmptyMessage.bind(this)
    this.onChangeEmptyMessage = this.onChangeEmptyMessage.bind(this)
    this.onSearchUpdated = this.onSearchUpdated.bind(this)
    this.selectUserRole = this.selectUserRole.bind(this)
    this.removeExcludedUserRole = this.removeExcludedUserRole.bind(this)
    this.onChangeShowLink = this.onChangeShowLink.bind(this)
    this.onChangeLinkText = this.onChangeLinkText.bind(this)
    this.onChangeShowLoggedInUserSection = this.onChangeShowLoggedInUserSection.bind(this)
  }

  async componentDidMount () {
    const result = await axios.get('/role/fetchRoles')
    const { mainRoles, customRoles } = result.data
    let roles = mainRoles
    if (customRoles) {
      roles = roles.concat(customRoles)
    }
    this.setState({
      allUserRoles: roles
    })
  }

  selectUserRole (userRole: UserRole) {
    const excludeUserRole = [userRole.name].concat(this.state.excludeUserRole)

    this.setState({
      excludeUserRole: excludeUserRole,
      searchText: ''
    }, () => this.onChangeSend())
  }

  removeExcludedUserRole (userRole: UserRole) {
    const newExcludeUserRole = this.state.excludeUserRole.filter(excludedRole => userRole.name !== excludedRole)

    this.setState({
      excludeUserRole: newExcludeUserRole
    }, () => this.onChangeSend())
  }

  onChangeTitle (newTitle: string) {
    this.setState({
      title: newTitle
    }, () => this.onChangeSend())
  }

  onSearchUpdated (newSearchText: string) {
    this.setState({
      searchText: newSearchText.toLowerCase(),
      inputInFocus: true
    })
  }

  onChangeEmptyMessage (newMessage: string) {
    this.setState({
      emptyMessage: newMessage
    }, () => this.onChangeSend())
  }

  onChangeMaxResults (newMaxResults: number) {
    this.setState({
      maxResults: newMaxResults
    }, () => this.onChangeSend())
  }

  onChangeCheckbox (name: TimeRanges, isChecked: boolean) {
    const newTimeRange = new Set(this.state.timeRange)
    if (isChecked) {
      newTimeRange.add(name)
    } else {
      newTimeRange.delete(name)
    }

    this.setState({
      timeRange: allTimeRanges.filter(range => newTimeRange.has(range))
    }, () => this.onChangeSend())
  }

  onChangeIsPreviewEmptyMessage (isChecked: boolean) {
    this.setState({
      isPreviewEmptyMessage: isChecked
    }, () => this.onChangeSend())
  }

  onChangeShowLink (isChecked: boolean) {
    this.setState({
      showLink: isChecked
    }, () => this.onChangeSend())
  }

  onChangeShowLoggedInUserSection (isChecked: boolean) {
    this.setState({
      showLoggedInUserSection: isChecked
    }, () => this.onChangeSend())
  }

  onChangeLinkText (newLinkText: string) {
    this.setState({
      linkText: newLinkText
    }, () => this.onChangeSend())
  }

  onChangeSend () {
    const newData: NewData = {
      title: this.state.title,
      maxResults: this.state.maxResults,
      excludeUserRole: this.state.excludeUserRole,
      timeRange: this.state.timeRange,
      emptyMessage: this.state.emptyMessage,
      isPreviewEmptyMessage: this.state.isPreviewEmptyMessage,
      showLink: this.state.showLink,
      linkText: this.state.linkText,
      showLoggedInUserSection: this.state.showLoggedInUserSection
    }

    this.props.onChange(newData)
  }

  render ({ controlUrl }: Props, {
    title,
    maxResults,
    timeRange,
    emptyMessage,
    searchText,
    isPreviewEmptyMessage,
    excludeUserRole,
    inputInFocus,
    showLink,
    linkText,
    isContentPanelActive,
    isUsersPanelActive,
    isEmptyStatusPanelActive,
    showLoggedInUserSection
  }: State) {
    const roles = this.state.allUserRoles.filter(role => {
      if (this.state.excludeUserRole.includes(role.name)) return false
      return role.label.toLowerCase().includes(this.state.searchText)
    })

    return (
      <div className='widget-configurator_leaderboard-config-tabs-wrapper'>
        <ConfigurationPanel
          isActive={isContentPanelActive}
          title='CONTENT'
          togglePanel={(isContentPanelActive) => this.setState({ isContentPanelActive: !isContentPanelActive })}
        >
          <div className='widget-configurator_tab-content-custom'>
            <InputGroup
              title='Title'
              type='text'
              onChange={(e: {target: HTMLInputElement}) => { this.onChangeTitle(e.target.value) }}
              value={title}
            />

            <div class='leaderboard-config__time-range'>
              <label>Time range</label>
              {allTimeRanges.map(item => (
                <InputGroup
                  key={item}
                  inline
                  title={<Phrase p={`leaderboard.points.${item}`} />}
                  type='checkbox'
                  checked={timeRange.includes(item) ? 'checked' : ''}
                  onChangeCheckbox={(e: {target: HTMLInputElement}) => { this.onChangeCheckbox(item, e.target.checked) }}
                />
              ))}
              <p class={`leaderboard-config__time-range-error ${!timeRange.length ? 'leaderboard-config__time-range-error--active' : ''}`}>Please select at least one option</p>
            </div>
            <div class='leaderboard-config__logged-in-user-section'>
              <label>Current logged-in user</label>
              <InputGroup
                inline
                title='Show current logged-in user’s ranking and points if they are included in the leaderboard'
                type='checkbox'
                checked={showLoggedInUserSection ? 'checked' : ''}
                onChangeCheckbox={(e: { target: HTMLInputElement }) => { this.onChangeShowLoggedInUserSection(e.target.checked) }}
              />
            </div>
            <div class='leaderboard-config__link'>
              <label>Link to leaderboard page</label>
              <InputGroup
                inline
                title='Show a link to leaderboard page'
                type='checkbox'
                checked={showLink ? 'checked' : ''}
                onChangeCheckbox={(e: { target: HTMLInputElement }) => { this.onChangeShowLink(e.target.checked) }}
              />
            </div>
            {showLink && <div class='leaderboard-config__link-text'>
              <InputGroup
                title='Link text'
                type='text'
                onChange={(e: { target: HTMLInputElement }) => { this.onChangeLinkText(e.target.value) }}
                value={linkText}
              />
              <Tip message={<div>
                <div className='widget-configuration_tip-box-title'>🤔 How do I configure the leaderboard page?</div>
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <div>You can exclude user roles from the leaderboard page <a target='_blank' href={`${controlUrl}/settings/pointSystem`}>in control</a>. For a good end user UX, we recommend keeping the same user role configuration for the leaderboard page and the widget where you enable this link.</div>
              </div>}
              />
            </div>}
          </div>
        </ConfigurationPanel>
        <ConfigurationPanel
          isActive={isUsersPanelActive}
          title='USERS'
          togglePanel={(isUsersPanelActive) => this.setState({ isUsersPanelActive: !isUsersPanelActive })}
        >
          <div className='widget-configurator_tab-content-custom'>
            <InputGroup
              title='Maximum amount of users'
              type='number'
              onChange={(e: {target: HTMLInputElement}) => { this.onChangeMaxResults(parseInt(e.target.value, 10)) }}
              value={maxResults}
              min={1}
            />
            <div className='form-group leaderboard-config__user-roles'>
              <label>Exclude user role</label>
              <div>
                {
                  excludeUserRole.map(roleName => {
                    const userRole = this.state.allUserRoles.filter(({ name }) => name === roleName)[0]
                    if (!userRole) return null
                    return (
                      <span key={roleName} className='leaderboard-config__user-roles_selected-role'>{userRole.label} <span
                        className='leaderboard-config__user-role-type'
                                                                                                                     > ({userRole.isMain ? 'Main' : 'Custom'})</span>
                        <span
                          className='leaderboard-config__user-roles_selected-role-remove-button'
                          onClick={() => this.removeExcludedUserRole(userRole)}
                        >
                          <Icon icon='close' width={16} height={16} color='currentColor' />
                        </span>
                      </span>
                    )
                  })
                }
              </div>

              <div className='leaderboard-config__user-roles-selection'>
                <input
                  title=''
                  className='form-control'
                  placeholder='Select user role'
                  type='text'
                  onFocus={() => this.setState({ inputInFocus: true })}
                  onBlur={() => setTimeout(() => {
                    this.setState({ inputInFocus: false })
                  }, 100)}
                  onInput={e => {
                    const target = e?.target
                    if (!isInputEl(target)) return
                    this.onSearchUpdated(target.value)
                  }}
                  value={searchText}
                />

                {
                  inputInFocus && <div className='leaderboard-config__user-role-list'>
                    {roles.length
                      ? (

                        <ul>
                          {
                          roles.map(role => (
                            <li
                              key={role.name}
                              className='leaderboard-config__user-role'
                              onMouseDown={() => this.selectUserRole(role)}
                            >{role.label}
                              <span
                                className='leaderboard-config__user-role-type'
                              > ({role.isMain ? 'Main' : 'Custom'})</span>
                            </li>
                          ))
                        }
                        </ul>
                        )
                      : <li className='leaderboard-config__user-role_not-found'>No results found</li>}
                  </div>
                }
              </div>
            </div>
            <Tip message={<div>
              <div className='widget-configuration_tip-box-title'>🤔 Why is my leaderboard empty?</div>
              {/* eslint-disable-next-line react/jsx-no-target-blank */}
              <div>Please make sure that you enabled point system rules in <a target='_blank' href={`${controlUrl}/settings/pointSystem`}>point system page in control</a>.</div>
            </div>}
            />
          </div>
        </ConfigurationPanel>
        <ConfigurationPanel
          isActive={isEmptyStatusPanelActive}
          title='EMPTY STATUS'
          togglePanel={(isEmptyStatusPanelActive) => this.setState({ isEmptyStatusPanelActive: !isEmptyStatusPanelActive })}
        >
          <div className='widget-configurator_tab-content-custom'>
            <div className='leaderboard-config__empty-status'>
              <label>Empty status</label>
              <p className='leaderboard-config__empty-status-description'>Add a message to be shown when there are fewer
                than 3 users on leaderboard by custom HTML</p>

              <CodeMirror
                defaultValue={emptyMessage}
                onChange={(html: string) => this.onChangeEmptyMessage(html)}
                options={{
                  lineNumbers: true,
                  mode: 'text/html',
                  selectionPointer: true,
                  autoRefresh: true,
                  autoResize: true
                }}
              />

              <InputGroup
                inline
                title='Show empty status in preview'
                type='checkbox'
                checked={isPreviewEmptyMessage ? 'checked' : ''}
                onChangeCheckbox={(e: { target: HTMLInputElement }) => {
                  this.onChangeIsPreviewEmptyMessage(e.target.checked)
                }}
                value='preview'
              />
            </div>
          </div>
        </ConfigurationPanel>
      </div>
    )
  }
}
